import { Component, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-daily-battery-usage-report',
	templateUrl: './daily-battery-usage-report.component.html',
	styleUrls: ['./daily-battery-usage-report.component.css']
})
export class DailyBatteryUsageReportComponent {
	@Input() device: any = {};
	@Input() currentSite: any = {};
	@Input() hasVoltageFilter = true;
	@Input() hasCapacityFilter = true;
	@Input() hasVintageFilter = true;
	@Input() hasManufacturerFilter = true;
	@Input() columnsTooltips: any = {};
	siteId = 0;

	maxDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	toDate = this.maxDate; // yesterday
	fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	minDate = this.fromDate;

	batteryVoltagesOptions: any[] = [];
	selectedBatteryVoltage: string = 'all';

	batteryCapacitiesOptions: any[] = [];
	selectedBatteryCapacity: string = 'all';
	isDevicePage = false;

	vintageOptionsDropdown = this.siteDashboardService.vintageOptionsDropdown;
	selectedBatteryVintage: string[] = ['all'];

	batteryManufacturersOptions: any[] = [];
	selectedBatteryManufacturer: string = 'all';

	columnConfig: ColumnsConfig[] = [];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		hideNoData: true,
		draggable: true,
		fileName: this.translate.instant('fleet_reports.daily_battery_usage_report'),
		csvFileHeaderInfo: []
	};

	excludeColumnsForDevicePage = ['batteries_count', 'used_batteries_count'];
	excludeColumnsForSitePage = ['battery_ah_return_score'];

	constructor(
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private siteDashboardService: SiteDashboardService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		this.isDevicePage = this.device?.mac_address ? true : false;
		this.siteId = this.route.parent.snapshot.params.siteId || 0;

		if (this.device && this.device?.installation_date) {
			this.minDate = moment.utc(this.device.installation_date * 1000).startOf('day').toDate();
			this.getDailyBatteriesUsageReport();
		} else {
			this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
				if (siteInfo && siteInfo.installation_date) {
					this.minDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
					this.getDailyBatteriesUsageReport();
				}
			});
		}

		this.prepareColumnConfig();
	}

	prepareColumnConfig() {
		const initialColumnsConfig: ColumnsConfig[] = [
			{ key: 'day', name: this.translate.instant('g.date'), type: 'date' },
			{ key: 'batteries_count', name: this.translate.instant('fleet_reports.batteries_count'), type: 'number' },
			{ key: 'used_batteries_count', name: this.translate.instant('fleet_reports.used_batteries_count'), type: 'number', hasTooltip: this.columnsTooltips.used_batteries_count ? this.translate.instant(this.columnsTooltips.used_batteries_count) : null }
		];

		if (!this.isDevicePage) {
			initialColumnsConfig.push(
				{ key: 'usage_score', name: this.translate.instant('fleet_reports.utilization_score_percent'), type: 'number', hasTooltip: this.columnsTooltips.usage_score ? this.translate.instant(this.columnsTooltips.usage_score) : null },
			);
		}

		initialColumnsConfig.push(
			{ key: 'energy_usage_score', name: this.translate.instant('fleet_reports.energy_usage_score'), type: 'number', hasTooltip: this.columnsTooltips.energy_usage_score ? this.translate.instant(this.columnsTooltips.energy_usage_score) : null },
			{ key: 'total_discharged_ah', name: this.translate.instant('truck_performance.total_discharged_ah'), type: 'number' },
			{ key: 'total_charge_ah', name: this.translate.instant('fleet_reports.total_charge_ah'), type: 'number' },
			{ key: 'battery_ah_return_score', name: this.translate.instant('utilization.battlink_ah_return_score_percentage'), type: 'number', hasTooltip: this.columnsTooltips.battery_ah_return_score ? this.translate.instant(this.columnsTooltips.battery_ah_return_score) : null },
			{ key: 'total_inuse_hours', name: this.translate.instant('fleet_reports.total_inuse_hours'), type: 'number' },
			{ key: 'total_charge_hours', name: this.translate.instant('fleet_reports.total_charge_hours'), type: 'number' },
			{ key: 'total_idle_hours', name: this.translate.instant('fleet_reports.total_idle_hours'), type: 'number' },
		);

		if (this.isDevicePage)
			this.columnConfig = initialColumnsConfig.filter(col => !this.excludeColumnsForDevicePage.includes(col.key));
		else
			this.columnConfig = initialColumnsConfig.filter(col => !this.excludeColumnsForSitePage.includes(col.key));
	}

	changeDate(field, value) {
		this[field] = value;

		this.getDailyBatteriesUsageReport();
	}

	getDailyBatteriesUsageReport() {
		const zoneDiff	= new Date().getTimezoneOffset() * -1;
		let fromDate:any	= new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate:any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		let options: any = {};
		if (this.isDevicePage) {
			options = { mac_address: this.device.mac_address };
		} else {
			options = {
				batteryVoltage: this.selectedBatteryVoltage,
				batteryCapacity: this.selectedBatteryCapacity,
				batteryVintage: this.selectedBatteryVintage,
				batteryManufacturer: this.selectedBatteryManufacturer,
			};
		}

		this.sitesService.getDailyBatteriesUsageReport(fromDate, toDate, this.siteId, options).subscribe((res: any) => {
			const tableData = [];

			if (!this.isDevicePage) {
				this.batteryVoltagesOptions = this.commonService.prepareOptions(res.batteryVoltages);
				this.batteryCapacitiesOptions = this.commonService.prepareOptions(res.batteryCapacities);
				this.batteryManufacturersOptions = this.commonService.prepareOptions(res.batteryManufacturers);
			}

			res = res.daily || {};

			for (const day in res) {
				if (res.hasOwnProperty(day) && res[day].day) {
					const data = {
						day: {value: res[day].day},
						batteries_count: {value: res[day].batteries_count},
						used_batteries_count: {value: res[day].used_batteries_count},
						usage_score: {value: this.siteDashboardService.calculateUsageScore(res[day].batteries_count, res[day].used_batteries_count)},
						energy_usage_score: {value: Math.round(res[day].energy_usage_score)},
						total_discharged_ah: {value: Math.round(res[day].total_inuse_as / 3600 || 0).toLocaleString()},
						total_charge_ah: {value: Math.round(res[day].total_charge_as / 3600 || 0).toLocaleString()},
						battery_ah_return_score: {value: this.siteDashboardService.calculateBatteryAhScore(res[day].total_charge_as, res[day].total_inuse_as)},
						total_inuse_hours: {value: Math.round((res[day].total_inuse_second || 0) / 3600 || 0).toLocaleString()},
						total_charge_hours: {value:  Math.round((res[day].total_charge_second || 0) / 3600 || 0).toLocaleString()},
						total_idle_hours: {value:  Math.round((res[day].total_idle_second || 0) / 3600 || 0).toLocaleString()},
					};

					const excludeKeys = this.isDevicePage ? this.excludeColumnsForDevicePage : this.excludeColumnsForSitePage;
					for (const key of excludeKeys)
						delete data[key];

					tableData.push(data);
				}
			}

			this.tableData = tableData;
			this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
				customerName: this.currentSite.customer_name,
				siteName: this.currentSite.name,
				deviceSN: this.device.serial_number,
				includeSpacing: false
			})
		});
	}

	batteryVintageSelectionChanged() {
		if (!this.selectedBatteryVintage.length)
			this.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];

		if (this.selectedBatteryVintage.length > 1 && this.selectedBatteryVintage.includes(this.siteDashboardService.vintageOptionsDropdown[0].value)) {
			if (this.selectedBatteryVintage[0] == this.siteDashboardService.vintageOptionsDropdown[0].value)
				this.selectedBatteryVintage = this.selectedBatteryVintage.filter(value => value !== this.siteDashboardService.vintageOptionsDropdown[0].value);
			else
				this.selectedBatteryVintage = [this.siteDashboardService.vintageOptionsDropdown[0].value];
		}

		this.getDailyBatteriesUsageReport();
	}
}
